import { ExternalLink, Info, InfoIcon, Globe, Database } from 'lucide-react'
import { FC } from 'react'

import Field from '@/components/field'
import LoadingButton from '@/components/loading-button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import SettingsCard from '@/components/settings-card'

import { ActionTimer } from '@/hooks/useActionTimer'
import { FormData } from './'
import HeadersForm from './HeadersForm'
import RemoveButton from './RemoveButton'

type Props = {
  timer: ActionTimer
  onRemove: () => void
}

const FormCard: FC<Props> = ({ timer, onRemove }) => {
  return (
    <SettingsCard
      Icon={Globe}
      title="Customer Lookup Configuration"
      description="Configure your API endpoint and data mapping"
      className="max-w-lg"
      sections={[
        <div key="api-endpoint" className="space-y-4">
          <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">API Endpoint</h3>

          <Field<FormData>
            label="Endpoint URL"
            name="url"
            placeholder="https://example.com/"
            testId="endpoint-url-input"
          />

          <div className="space-y-2">
            <Label className="text-sm font-medium">Method</Label>
            <Field<FormData> label={false} name="method">
              {field => (
                <RadioGroup
                  className="flex items-center gap-4"
                  onValueChange={field.onChange}
                  value={field.value as string}
                >
                  <div className="flex items-center space-x-2 cursor-pointer">
                    <RadioGroupItem value="GET" id="GET" />
                    <Label htmlFor="GET" className="flex items-center text-sm cursor-pointer">
                      GET
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger>
                            <InfoIcon className="w-3 h-3 ml-1" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <code>?phoneNumber=13035551234</code>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 cursor-pointer">
                    <RadioGroupItem value="POST" id="POST" />
                    <Label htmlFor="POST" className="flex items-center text-sm cursor-pointer">
                      POST
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger>
                            <InfoIcon className="w-3 h-3 ml-1" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <code>{'{ "phoneNumber": "+13035551234" }'}</code>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                  </div>
                </RadioGroup>
              )}
            </Field>
          </div>

          <div>
            <Label className="text-sm font-medium">Headers (Optional)</Label>
            <HeadersForm />
          </div>
        </div>,

        <div key="data-mapping" className="space-y-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold tracking-wide uppercase text-muted-foreground">
            <Database className="w-4 h-4" />
            Data Mapping
          </h3>

          <div className="grid gap-3 md:grid-cols-2">
            <Field<FormData>
              label="Customer Path"
              name="customerPath"
              placeholder="data.customers"
              testId="customer-path-input"
              aside={
                <a
                  href="https://github.com/tidwall/gjson?tab=readme-ov-file#path-syntax"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:underline"
                >
                  GJSON syntax <ExternalLink className="w-3 h-3" />
                </a>
              }
            />

            <Field<FormData> label="ID Path" name="customerId" placeholder="id" testId="customer-id-input" />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Field<FormData>
              label={
                <span className="flex items-center">
                  Title Template
                  <DynamicContentTooltip />
                </span>
              }
              name="titleTemplate"
              placeholder="{firstName} {lastName}"
              testId="title-template-input"
            />

            <Field<FormData>
              label={
                <span className="flex items-center">
                  Description Template
                  <DynamicContentTooltip />
                </span>
              }
              name="descriptionTemplate"
              placeholder="{streetName} {zipCode}"
              testId="description-template-input"
            />
          </div>
        </div>,
      ]}
      footer={
        <div className="flex items-center justify-between w-full">
          <RemoveButton onRemove={onRemove} />
          <LoadingButton data-testid="save-configuration-button" timer={timer} type="submit" variant="default">
            Save Configuration
          </LoadingButton>
        </div>
      }
    />
  )
}

export default FormCard

const DynamicContentTooltip: FC = () => (
  <TooltipProvider delayDuration={0}>
    <Tooltip>
      <TooltipTrigger tabIndex={-1}>
        <Info className="w-3 h-3 ml-2" />
      </TooltipTrigger>
      <TooltipContent>
        <p>Use {'{fieldName}'} for dynamic content.</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)
